/**
 * Pricing Overrides Module
 * 
 * Handles matching user-provided installation prices to system options
 * and calculating total costs over a given period.
 */

import type { SystemRecInput, RecommendationOption, CurrencyCode } from './types';

/**
 * Normalize variant to standard or mixergy
 */
export function normalizeVariant(variant: string | undefined): 'standard' | 'mixergy' {
  if (variant === 'mixergy') {
    return 'mixergy';
  }
  return 'standard';
}

/**
 * Resolve currency from entry and override settings
 */
export function normalizeCurrency(
  overrideCurrency: CurrencyCode | undefined,
  entryCurrency: CurrencyCode | undefined
): CurrencyCode {
  // Entry-level currency takes precedence
  if (entryCurrency) {
    return entryCurrency;
  }
  // Then override-level currency
  if (overrideCurrency) {
    return overrideCurrency;
  }
  // Default to GBP
  return 'GBP';
}

/**
 * Check if a number is finite and non-negative
 */
export function isFiniteNonNegative(n: unknown): boolean {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0;
}

/**
 * Match result type
 */
export type MatchResult = {
  capex?: number;
  match: 'ID_EXACT' | 'EXACT' | 'FALLBACK_NO_VARIANT' | 'NONE';
  matchedOverrideId?: string;
  currency?: CurrencyCode;
};

/**
 * Match a capex override to an option
 * 
 * Matching priority:
 * 1. ID_EXACT: Override ID matches option.id (for stable IDs)
 * 2. EXACT: heatSource + strategy + variant all match
 * 3. FALLBACK_NO_VARIANT: heatSource + strategy match (ignoring variant)
 * 4. NONE: No match found
 */
export function matchCapex(
  option: RecommendationOption,
  pricingOverrides: SystemRecInput['pricingOverrides']
): MatchResult {
  if (!pricingOverrides?.entries?.length) {
    return { match: 'NONE' };
  }

  // Extract option's heat source (boiler type)
  const optionHeatSource = option.boiler; // e.g., 'regular', 'system', 'combi'
  
  // Extract option's hot water strategy
  // Parse from option.water which represents the strategy
  let optionStrategy = option.water;
  
  // For variant, check waterLabel for 'Mixergy' or if water field contains 'mixergy_'
  // The water field may have legacy formats like 'mixergy_unvented'
  let optionVariant: 'standard' | 'mixergy' = 'standard';
  if (option.waterLabel?.toLowerCase().includes('mixergy')) {
    optionVariant = 'mixergy';
  } else if (option.water.includes('mixergy')) {
    // Handle legacy format like 'mixergy_unvented'
    optionVariant = 'mixergy';
    // Extract the actual strategy from the water field
    if (option.water.startsWith('mixergy_')) {
      optionStrategy = option.water.replace('mixergy_', '');
    }
  }

  // 1. Try ID_EXACT match first (only if option.id looks like a stable ID, not a generated one)
  for (const entry of pricingOverrides.entries) {
    if (entry.id === option.id) {
      // Validate the price
      if (!isFiniteNonNegative(entry.installedPrice)) {
        continue;
      }
      
      const currency = normalizeCurrency(pricingOverrides.currency, entry.currency);
      
      return {
        capex: entry.installedPrice,
        match: 'ID_EXACT',
        matchedOverrideId: entry.id,
        currency
      };
    }
  }

  // 2. Try EXACT match: heatSource + strategy + variant
  for (const entry of pricingOverrides.entries) {
    const entryVariant = normalizeVariant(entry.hotWater.variant);
    
    // Match heat source (case-insensitive comparison)
    const heatSourceMatch = entry.heatSource.toLowerCase() === optionHeatSource.toLowerCase();
    
    // Match strategy (case-insensitive comparison)
    const strategyMatch = entry.hotWater.strategy.toLowerCase() === optionStrategy.toLowerCase();
    
    // Match variant
    const variantMatch = entryVariant === optionVariant;
    
    if (heatSourceMatch && strategyMatch && variantMatch) {
      // Validate the price
      if (!isFiniteNonNegative(entry.installedPrice)) {
        continue;
      }
      
      const currency = normalizeCurrency(pricingOverrides.currency, entry.currency);
      
      return {
        capex: entry.installedPrice,
        match: 'EXACT',
        matchedOverrideId: entry.id,
        currency
      };
    }
  }

  // 3. Try FALLBACK_NO_VARIANT: heatSource + strategy (ignoring variant)
  for (const entry of pricingOverrides.entries) {
    // Match heat source
    const heatSourceMatch = entry.heatSource.toLowerCase() === optionHeatSource.toLowerCase();
    
    // Match strategy
    const strategyMatch = entry.hotWater.strategy.toLowerCase() === optionStrategy.toLowerCase();
    
    if (heatSourceMatch && strategyMatch) {
      // Validate the price
      if (!isFiniteNonNegative(entry.installedPrice)) {
        continue;
      }
      
      const currency = normalizeCurrency(pricingOverrides.currency, entry.currency);
      
      return {
        capex: entry.installedPrice,
        match: 'FALLBACK_NO_VARIANT',
        matchedOverrideId: entry.id,
        currency
      };
    }
  }

  // 4. No match found
  return { match: 'NONE' };
}

/**
 * Collect IDs of unused override entries
 */
export function collectUnusedOverrides(
  pricingOverrides: SystemRecInput['pricingOverrides'],
  usedIds: Set<string>
): string[] {
  if (!pricingOverrides?.entries?.length) {
    return [];
  }

  return pricingOverrides.entries
    .map(entry => entry.id)
    .filter(id => !usedIds.has(id));
}
